import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrdersFilters, OrdersTable } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  CustomerOrder,
  backendStatusToUi,
  backendInvoiceStatusToPayment,
} from './customerOrdersTypes';
import { useOrdersStore } from '../../../store/ordersStore';
import { Order, getOrder } from '../../../services/ordersApi';
import { useCartStore } from '../../../store/cartStore';
import { formatOrderId } from '../../../utils/formatId';
import styles from './CustomerOrders.module.css';

function adapt(o: Order): CustomerOrder {
  return {
    id: String(o.id),
    orderNumber: formatOrderId(o.id),
    date: new Date(o.order_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    total: `$${Number(o.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    orderStatus: backendStatusToUi(o.status),
    paymentStatus: backendInvoiceStatusToPayment(o.invoice?.status),
  };
}

const CustomerOrders: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('All Status');
  const [date, setDate] = useState('');
  const [reorderingId, setReorderingId] = useState<string | null>(null);
  const [reorderMessage, setReorderMessage] = useState<string | null>(null);

  const items = useOrdersStore((s) => s.items);
  const loading = useOrdersStore((s) => s.loading);
  const error = useOrdersStore((s) => s.error);
  const setSource = useOrdersStore((s) => s.setSource);
  const resetFilters = useOrdersStore((s) => s.resetFilters);
  const fetchList = useOrdersStore((s) => s.fetchList);
  const addToCart = useCartStore((s) => s.addItem);

  useEffect(() => {
    setSource('customer-self');
    // Drop any filter (e.g. status='pending') left from a prior page so the
    // customer sees all of their orders, not just one status bucket.
    resetFilters();
    fetchList();
  }, [setSource, resetFilters, fetchList]);

  const adapted = useMemo<CustomerOrder[]>(() => items.map(adapt), [items]);

  const filtered = useMemo(() => {
    return adapted.filter((o) => {
      const matchesStatus = status === 'All Status' || o.orderStatus === status;
      const matchesDate = !date || o.date.includes(date);
      return matchesStatus && matchesDate;
    });
  }, [adapted, status, date]);

  // VP-CUST-001: explicit "Reorder" action.
  //
  // POST /orders/:id/duplicate is Admin/Sales Rep/Call Center only by design
  // (routes/orders.ts: "Customer self-duplicate would need to go through the
  // customer order flow which has stricter validations") — confirmed via a
  // live 403 while wiring this up. So a Customer reorder instead goes
  // through that same normal flow: pull the source order's real line items
  // (GET /orders/:id, which IS ownership-checked for Customers) into the
  // cart, then let the existing cart/checkout page place the new order with
  // its usual validation (stock, pricing, address).
  const handleReorder = async (orderId: string) => {
    setReorderingId(orderId);
    setReorderMessage(null);
    try {
      const source = await getOrder(orderId);
      const lines = (source.items ?? []).filter((it) => it.sku_id);
      if (lines.length === 0) {
        setReorderMessage('That order has no items to reorder.');
        return;
      }
      for (const line of lines) {
        addToCart(
          {
            sku_id: line.sku_id,
            sku_code: line.sku_code ?? String(line.sku_id),
            name: line.product_name ?? `SKU ${line.sku_id}`,
            unit_price: Number(line.unit_price) || 0,
          },
          line.quantity
        );
      }
      setReorderMessage(`Added ${lines.length} item(s) from ${formatOrderId(source.id)} to your cart.`);
      navigate('/customer-cart');
    } catch (err) {
      setReorderMessage(err instanceof Error ? err.message : 'Failed to reorder');
    } finally {
      setReorderingId(null);
    }
  };

  return (
    <div className={styles.container}>
      <OrdersFilters
        status={status}
        onStatusChange={setStatus}
        date={date}
        onDateChange={setDate}
      />
      {reorderMessage && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: '#EFF6FF', color: '#1D4ED8', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
          {reorderMessage}
        </div>
      )}
      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && filtered.length === 0}
        emptyMessage="You have no orders yet"
        onRetry={() => fetchList()}
      >
        <OrdersTable
          orders={filtered}
          onViewDetails={(id) => navigate(`/customer-order-details?id=${id}`)}
          onReorder={handleReorder}
          reorderingId={reorderingId}
        />
      </AsyncBoundary>
    </div>
  );
};

export const CustomerOrdersPage: React.FC = () => {
  return <CustomerOrders />;
};

export { CustomerOrders };
export default CustomerOrders;
